import { cn } from '@lombok/ui';

/**
 * Shimmering placeholder block. Compose into shapes that mirror the real layout
 * so loading reads as "content arriving" rather than a generic spinner. The
 * shimmer + reduced-motion handling live in the `.skeleton` utility (index.css).
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded-md', className)} aria-hidden="true" />;
}

/**
 * Loading shape for list/table screens: a header row plus N body rows on
 * desktop, and stacked two-line cards on mobile — the same responsive split the
 * real lists use. Announced politely to screen readers.
 */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="p-4" aria-busy="true" role="status">
      <span className="sr-only">Cargando…</span>

      {/* Desktop table shape */}
      <div className="hidden sm:block">
        <div className="flex items-center gap-4 border-b border-border pb-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-3', c === 0 ? 'w-32' : 'w-20')} />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 border-b border-border py-3.5 last:border-0">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={c} className={cn('h-4', c === 0 ? 'w-44' : 'w-24 max-w-[40%] flex-1')} />
            ))}
          </div>
        ))}
      </div>

      {/* Mobile card shape */}
      <ul className="divide-y divide-border sm:hidden">
        {Array.from({ length: Math.min(rows, 5) }).map((_, r) => (
          <li key={r} className="flex flex-col gap-2 py-4">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="h-3 w-1/3" />
          </li>
        ))}
      </ul>
    </div>
  );
}
